/* eslint-disable no-undef */
/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React from 'react';
import { Box } from '@mui/material';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import { useEffect, useState } from 'react';
import { withTheme } from 'styled-components';
import FlightLeg from '../../../shared/entities/flightLeg';
import FlightLegServiceAlerts, { Waypoint } from '../../../shared/entities/flightLegServiceAlerts';
import { selectFlightPathWaypointsChecked } from '../../../store';
import { minutesToFormattedMinutesStringColonDelimitedFormat } from '../../../utils/DateTimeUtils';
import { useSelectorWithMapId } from '../../common/map/BaseMapContainer';
import appTheme from '../../common/theme/appTheme.json';
import WaypointDialog from './WaypointDialog';
export interface FlightPathContainerStateProps {}

export interface FlightPathContainerProps {
  map: google.maps.Map;
  flightLeg: FlightLeg;
  flightLegServiceAlerts: { [flightLegId: number]: FlightLegServiceAlerts };
  overlaidFlightLegHidden?: boolean;
}

const MptFlightPathWaypointsContainer = ({
  map,
  flightLeg,
  flightLegServiceAlerts,
  overlaidFlightLegHidden,
}: FlightPathContainerProps) => {
  // The setFlightPathWaypointSymbols is the array of Symbols for flight nav waypoints along the mpt flight patah
  const [flightPathWaypointSymbols, setFlightPathWaypointSymbols] = useState<google.maps.Marker[]>([]);
  const [hoveredWaypoint, setHoveredWaypoint] = useState<Waypoint | undefined | null>();
  const flightPathWaypointsChecked = useSelectorWithMapId(selectFlightPathWaypointsChecked);

  useEffect(() => {
    if (
      !isNil(flightLeg.id) &&
      !isNil(flightLeg) &&
      !isNil(flightLegServiceAlerts) &&
      !isNil(flightLegServiceAlerts[flightLeg.id]) &&
      !overlaidFlightLegHidden &&
      flightPathWaypointsChecked
    ) {
      // draw the fligth leg
      if (flightLeg) {
        if (!isNil(flightLegServiceAlerts[flightLeg.id]) && size(flightLegServiceAlerts[flightLeg.id].waypoints)) {
          // Take in the processed flight path waypoints (an array of arrays of online segments) and render the corresponding
          // Polylines on the map
          if (!isNil(flightLegServiceAlerts[flightLeg.id]) && size(flightLegServiceAlerts[flightLeg.id].waypoints)) {
            setFlightPathWaypointSymbols(
              flightLegServiceAlerts[flightLeg.id].waypoints.map((waypoint: Waypoint) => {
                const marker = new google.maps.Marker({
                  position: { lat: waypoint.latitude, lng: waypoint.longitude },
                  zIndex: 2,
                  clickable: true,

                  icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: '#010101',
                    strokeColor: '#505050',
                    strokeWeight: 1,
                    scale: 4,
                    fillOpacity: 1,
                  },
                });
                marker.addListener('mouseover', () => {
                  setHoveredWaypoint(waypoint);
                });
                marker.addListener('mouseout', () => {
                  setHoveredWaypoint(null);
                });
                return marker;
              }),
            );
          }
        }
      }
    } else if (overlaidFlightLegHidden || !flightPathWaypointsChecked) {
      setFlightPathWaypointSymbols([]);
    }
  }, [flightLeg.id, flightLeg, flightLegServiceAlerts, overlaidFlightLegHidden, flightPathWaypointsChecked]);

  useEffect(() => {
    flightPathWaypointSymbols.forEach((flightPathWaypointSymbol: google.maps.Marker) => {
      flightPathWaypointSymbol.setMap(map);
    });

    return () => {
      flightPathWaypointSymbols.forEach((flightPathWaypointSymbol: google.maps.Marker) => {
        google.maps.event.clearListeners(flightPathWaypointSymbol, 'mouseover');
        google.maps.event.clearListeners(flightPathWaypointSymbol, 'mouseout');
        flightPathWaypointSymbol.setMap(null);
      });
    };
  }, [flightPathWaypointSymbols, map]);

  return !isNil(hoveredWaypoint) ? (
    <WaypointDialog
      key={'1'}
      map={map}
      visible={!isNil(hoveredWaypoint)}
      childComponent={
        <Box
          bgcolor={appTheme.primaryBackgroundColor}
          paddingY="12px"
          paddingX="24px"
          borderRadius="4px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <Box color={appTheme.primaryTextColor}>{hoveredWaypoint.name}</Box>
          <Box fontWeight="700" color={appTheme.primaryTextColor}>
            {`T+${minutesToFormattedMinutesStringColonDelimitedFormat(hoveredWaypoint.timeSinceTakeoffMin)}`}
            {hoveredWaypoint.timeSinceUserSetTakeoffMin
              ? ` (${minutesToFormattedMinutesStringColonDelimitedFormat(
                  hoveredWaypoint.timeSinceUserSetTakeoffMin,
                )}Z) `
              : ''}
          </Box>
          <Box color={appTheme.primaryTextColor}>
            {hoveredWaypoint.latitude.toFixed(2)}, {hoveredWaypoint.longitude.toFixed(2)}
          </Box>
          {/* altitudeFt is equal to -1 (MISSING_VALUE) if the flight plan doesn't include altitude information */}
          {hoveredWaypoint.altitudeFt !== -1 && (
            <Box color={appTheme.primaryTextColor}>{`${hoveredWaypoint.altitudeFt.toLocaleString()} ft`}</Box>
          )}
        </Box>
      }
      latLng={new google.maps.LatLng({ lat: hoveredWaypoint.latitude, lng: hoveredWaypoint.longitude })}
      veritcalOffsetPx={72}
    />
  ) : (
    <></>
  );
};

export default withTheme(MptFlightPathWaypointsContainer);
